import { api } from '@/config/api';

export const STOCK_UNITS = ['pcs', 'kg', 'g', 'l', 'ml', 'box', 'pack', 'dozen'] as const;
export type StockUnit = (typeof STOCK_UNITS)[number];

export const STOCK_MOVEMENT_TYPES = ['in', 'out', 'adjust', 'waste'] as const;
export type StockMovementType = (typeof STOCK_MOVEMENT_TYPES)[number];

export interface StockItem {
  stockUid: string;
  name: string;
  sku: string | null;
  category: string;
  unit: StockUnit;
  quantity: number;
  reorderLevel: number;
  reorderQuantity: number | null;
  costPerUnit: number;
  currency: string | null;
  supplier: string | null;
  catalogItemUid: string | null;
  isActive: boolean;
  low: boolean;
  value: number;
  lastMovement: { type: StockMovementType; quantity: number; quantityAfter: number; createdAt: string } | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface StockMovement {
  movementUid: string;
  stockUid: string;
  type: StockMovementType;
  quantity: number;
  quantityAfter: number;
  unitCost: number | null;
  note: string | null;
  userUid: string | null;
  createdAt: string | null;
}

export interface StockItemDetail {
  item: StockItem;
  usage7Days: number;
  usage30Days: number;
  movements: StockMovement[];
}

export interface StockSummary {
  items: number;
  lowStockCount: number;
  stockValue: number;
  wasteValueLast30Days: number;
  currency: string;
}

export interface StockCategoryRow { category: string; count: number }

export interface CreateStockItemInput {
  name: string;
  sku?: string;
  category?: string;
  unit: StockUnit;
  quantity?: number;
  reorderLevel?: number;
  reorderQuantity?: number;
  costPerUnit?: number;
  currency?: string;
  supplier?: string;
  catalogItemUid?: string;
  isActive?: boolean;
}

export type UpdateStockItemInput = Partial<CreateStockItemInput>;

export interface RecordMovementInput {
  stockUid: string;
  type: StockMovementType;
  /** Positive quantity for in/out/waste; the NEW absolute quantity for 'adjust'. */
  quantity: number;
  unitCost?: number;
  note?: string;
  /** Lets an out/waste movement go below zero. Company-admin only — enforced server-side. */
  allowNegative?: boolean;
}

export interface ListStockItemsParams {
  q?: string;
  category?: string;
  lowOnly?: 'true' | 'false';
  page?: number;
  limit?: number;
}

export interface ListMovementsParams {
  stockUid?: string;
  type?: StockMovementType;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export const stockService = {
  listItems: async (params?: ListStockItemsParams): Promise<{ items: StockItem[]; total: number; page: number; limit: number }> =>
    (await api.get('/slighter_crm/stock', { params })).data,

  getItem: async (stockUid: string): Promise<StockItemDetail> =>
    (await api.get(`/slighter_crm/stock/${stockUid}`)).data,

  createItem: async (p: CreateStockItemInput): Promise<StockItem> =>
    (await api.post('/slighter_crm/stock', p)).data,

  updateItem: async (stockUid: string, p: UpdateStockItemInput): Promise<StockItem> =>
    (await api.patch(`/slighter_crm/stock/${stockUid}`, p)).data,

  removeItem: async (stockUid: string): Promise<void> => { await api.delete(`/slighter_crm/stock/${stockUid}`); },

  categories: async (): Promise<{ items: StockCategoryRow[]; uncategorised: number }> =>
    (await api.get('/slighter_crm/stock/categories')).data,

  summary: async (): Promise<StockSummary> =>
    (await api.get('/slighter_crm/stock/summary')).data,

  listMovements: async (params?: ListMovementsParams): Promise<{ items: StockMovement[]; total: number; page: number; limit: number }> =>
    (await api.get('/slighter_crm/stock/movements', { params })).data,

  itemMovements: async (stockUid: string, params?: Omit<ListMovementsParams, 'stockUid'>): Promise<{ items: StockMovement[]; total: number; page: number; limit: number }> =>
    (await api.get(`/slighter_crm/stock/${stockUid}/movements`, { params })).data,

  recordMovement: async (p: RecordMovementInput): Promise<StockMovement> =>
    (await api.post('/slighter_crm/stock/movements', p)).data,
};

export const MOVEMENT_TYPE_LABEL: Record<StockMovementType, string> = {
  in: 'Stock in',
  out: 'Stock out',
  adjust: 'Adjust',
  waste: 'Waste',
};

export const UNIT_LABEL: Record<StockUnit, string> = {
  pcs: 'pcs', kg: 'kg', g: 'g', l: 'l', ml: 'ml', box: 'box', pack: 'pack', dozen: 'dozen',
};
